import type { Updater } from '@tanstack/react-table';
import { useCallback, useState } from 'react';

interface UseControllableStateArgs<T> {
  /** Controlled value from the parent. A non-`undefined` value puts the hook
   *  in controlled mode — it never mutates its own internal state. */
  value: T | undefined;
  /** Called with the fully-resolved next value whenever a change would occur,
   *  in both controlled and uncontrolled modes (mirrors `<input onChange>`).
   *  Typed with `| undefined` (not just `?:`) — exactOptionalPropertyTypes
   *  requires this since callers pass through their own optional prop, whose
   *  type already includes `undefined`. */
  onChange?: ((value: T) => void) | undefined;
  /** Seed value used only while uncontrolled. */
  defaultValue: T;
}

/**
 * Controlled-with-uncontrolled-fallback state, matching the native
 * value/onChange pattern. Returns a TanStack `Updater`-compatible setter so
 * it slots directly into `useReactTable`'s `onSortingChange`/`onPaginationChange`.
 */
export function useControllableState<T>({
  value,
  onChange,
  defaultValue,
}: UseControllableStateArgs<T>): [T, (updater: Updater<T>) => void] {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  const setValue = useCallback(
    (updater: Updater<T>) => {
      const next = typeof updater === 'function' ? (updater as (old: T) => T)(current) : updater;
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [current, isControlled, onChange],
  );

  return [current, setValue];
}
